/**
 * **流水页**（C 域 C1 · `/journal`）—— 列表 + 10 维筛选 + 「加载更多」 + 归因 + 详情抽屉
 *
 * 依据：C 域 PRD **§5.1**（行集与展示骨架）· **§5.1.3**（「加载更多」50 笔/批）· **§5.1.4**（10 维）·
 * **§5.1.10**（点行开抽屉）· **§5.3**（单框搜索）· L 域 PRD **§5.3**（归因）·
 * `AC-C1.1` / `C1.5` / `C1.6` / `C1.7` / `C1.8` / `C1.10` / `C1.13` / `C1.17` / `C1.33` · `AC-L3.4` / `L3.5`。
 *
 * ## 🔴 本页的六条纪律（每条都有出处）
 *
 * | # | 纪律 | 为什么 |
 * |:--:|---|---|
 * | 1 | **金额只用服务端派生的 `parent`**（`parent.net` / `parent.transfer`） | 工资条那种多笔跨账户的"各笔相加"没有语义（`DR-004`）；端侧**禁** `reduce` 求金额 |
 * | 2 | **一个日期都不算**（窗口边界交共享日历内核 + **账本时区**） | 与 `ContributionService` 同源；账本时区 ≠ 浏览器时区时会差一天 |
 * | 3 | **分组在端侧、且按"批"分组再拼接** | §5.1.2 要求「单日笔数**跨批次时重复出现分组头**」⇒ 合并后分组会让该条 AC 静默不成立 |
 * | 4 | **记录人徽标只在多人账本显示**（`AC-L3.4`） | 单人账本里"谁记的"恒等于自己，显示它只是噪音 |
 * | 5 | **抽屉内容抽成零 Portal 的组件**（`TransactionDetailContent`） | `Drawer` 走 Portal ⇒ 静态渲染基座测不到内容（㉝ 批同一课） |
 * | 6 | **「不计收支」的标注取服务端字段** `parent.hasExcludedLegs` | 端侧**不做形态判定**（判定唯一源 = `deriveParentRow`） |
 *
 * ## 🆕 批 `107`（K3 / K11）：批量闭环与冷启动
 *
 * | 能力 | 实现 | 依据 |
 * |---|---|---|
 * | 行首多选 + 吸顶批量工具条 + 全选本页 | 本页（选中态按 **`transactionId`**，且与筛选签名绑定） | `AC-C1.19` / `C1.20` |
 * | 改分类 / 改账户 / 加标签 / 去标签 / 删除 | 服务端四条已有接口；**拒因逐条原样展示** | `AC-C1.21` |
 * | 合计条 | 🔴 唯一实现 = 共享层 `nucleus/txn-summary.ts` 的 `summarizeSelection()`（本页只适配形状） | `AC-C1.24` · §5.1.9 |
 * | 示例数据 | 空账本才给入口；标记 = 「示例数据」标签；清除走 `bulk-delete` | `AC-B4.2` |
 * | 空态分流 | `total === 0` ⇒ 看示例 / 记第一笔；`total > 0` ⇒ 放宽筛选 | `AC-B4.7` |
 *
 * ## ⚠️ 仍未做（登记，不是漏项）
 * 编辑复用「记一笔」（`AC-C1.34`）· 虚拟滚动（`AC-C1.9` —— `DR-006` 允许「虚拟滚动**或**加载更多」二选一）·
 * 跨币种搜索框的键盘快捷键（`Ctrl K` 仍在顶栏未落地）。
 *
 * ## 🆕 批 `109`：工具条顺序
 * **筛选 → 统计 / 导出 → 搜索框（最后）**，面板内维度 **账户 → 时间 → 金额区间** 打头
 * （负责人：「筛选可以选择账户，日期，金额，然后再加最后的搜索框」）。顺序由
 * `ui/JournalFilterRail.spec.tsx` 与 `pages/JournalPage.spec.tsx` 各钉一条（它不会被类型检查守住）。
 */

import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ExportFormat,
  exportFileName,
  formatMoneyText,
  localDayOf,
  type TransactionRowView,
} from '@jiaying/shared';
import { downloadAuthorized } from '../api/download.js';
import {
  bulkDeleteTransactions,
  bulkPatchTransactions,
  createTag,
  createTransaction,
  listAccounts,
  listCategories,
  listCategoriesWithRemoved,
  listInstallments,
  listLedgerMembers,
  listTags,
  listTransactions,
  getPreferences,
} from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { useCurrentLedger } from '../app/useCurrentLedger.js';
import {
  DEMO_TAG,
  buildDemoTransactions,
  demoProbeQuery,
} from '../features/demo-data.js';
import { EXPORT_BUSY_LABEL, EXPORT_CURRENT_ACTION, EXPORT_FAILED_PREFIX } from '../features/export-labels.js';
import { invalidateAfterTxnWrite } from '../features/invalidate.js';
import {
  EMPTY_SELECTION,
  describeBulkResult,
  isAllLoadedSelected,
  multiLegCount,
  selectAllLoaded,
  summarizeSelected,
  toggleSelection,
} from '../features/journal-bulk.js';
import {
  activeDimensionCount,
  amountInputError,
  baseWindowOf,
  buildJournalQuery,
  exportCsvPath,
  initialJournalFilter,
  type JournalFilterDraft,
} from '../features/journal-filter.js';
import {
  DIRECTION_OPTIONS,
  chargeBackHint,
  excludedBadge,
  isFullyRefunded,
  refundHint,
  rowAmount,
  rowDiscount,
  rowSubtitle,
  rowTitle,
  windowOptionsFor,
} from '../features/journal-labels.js';
import { sectionizePages } from '../features/journal-sections.js';
import { canRefund, refundCategoryId } from '../features/refund-entry.js';
import { Amount } from '../ui/Amount.js';
import { AnchorBar } from '../ui/AnchorBar.js';
import { AccountPicker } from '../ui/AccountPicker.js';
import { CategoryPicker } from '../ui/CategoryPicker.js';
import { JournalFilterRail } from '../ui/JournalFilterRail.js';
import { Loading } from '../ui/Loading.js';
import { TransactionDrawer } from '../ui/TransactionDrawer.js';
import { TxnLegsList } from '../ui/TxnLegsList.js';

/**
 * 批量动作面板的四种形态（`null` = 未打开）。
 *
 * `tags` / `untags` 是两个独立面板：加标签要**挑**，去标签要**从已挂的里挑**——
 * 合成一个"改标签"会让用户分不清"我这次是加还是减"（而服务端是两个不同 action）。
 */
type BatchPanel = null | 'category' | 'account' | 'tags' | 'untags' | 'delete';

export function JournalPage() {
  const ledger = useCurrentLedger();
  const contentRef = useRef<HTMLDivElement>(null);
  const [search] = useSearchParams();
  // 🆕 分期标签跳转（`AC-B2.22`：点开可到计划）
  const navigate = useNavigate();

  /**
   * 草稿的**用户覆盖值**（`null` = 用户还没动过筛选 ⇒ 用基线**派生**）。
   *
   * 🔴 **不能只靠 `useState` 的惰性初值**：基线窗口来自**偏好查询**（`AC-N2.18`），
   * 首次渲染时它可能还没到（冷缓存直开 `/journal`）⇒ 初值会锁死在「近 3 月」。
   * ⇒ 渲染期 `draftOverride ?? initialJournalFilter(入口参数, baseWindow)` 派生：
   * 偏好一到，基线自动跟上（并触发一次换窗口的重新取数）；用户一旦动过筛选就以此为准。
   * （同 57/58 批的 `draftOverride ?? draftFrom(服务端值)` 定式。）
   */
  const [draftOverride, setDraftOverride] = useState<JournalFilterDraft | null>(null);
  const setDraft = setDraftOverride;
  /**
   * 🔴 批 `128`：支持 `?txn=<id>` **深链直开某一笔**（分期页「关联流水」的落点）。
   *
   * 为什么用抽屉而不是"筛选到那一笔"：抽屉走 `GET /api/transactions/:id`，**与列表窗口无关** ——
   * 一笔去年刷的卡分期、默认近 3 月的列表里根本没有它，靠筛选永远打不开。
   * 惰性初值即可：跳转来自别的页面 ⇒ 本组件会重新挂载；留在本页改 URL 不刷新也不算错
   * （用户此刻看的正是他自己刚点过的那一笔）。
   */
  const [selectedId, setSelectedId] = useState<string | null>(() => search.get('txn'));
  /**
   * 🆕 批 `115`（T7 · `DR-066` D3）：**行内展开态**（按 `transactionId`，不是下标）。
   *
   * 🔴 不持久化：刷新 / 换筛选后收起是**对的行为**（用户回来时看到的是干净的列表）。
   * 🔴 只对多笔行有值 —— 单腿行不渲染展开按钮（`row.legs.length > 1`）。
   */
  const [expandedRows, setExpandedRows] = useState<ReadonlySet<string>>(() => new Set<string>());
  /** 🔴 筛选下拉面板的展开态（`DR-042` · 负责人要求「下拉筛选」）—— **默认收起** */
  const [panelOpen, setPanelOpen] = useState(false);
  /** 🆕 导出当前筛选（`AC-N4.4`）的忙碌/失败态 —— 与列表**互不影响** */
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  /**
   * 🆕 批 `107`（K3）：批量选中态。
   *
   * 🔴 **以 `transactionId` 为准，且与「筛选签名」绑定**：
   * 一旦筛选变了（换窗口 / 改账户 / 搜索），旧选中集对应的行可能已经不在列表里 ——
   * 继续保留会让"已选 3 笔"里的两笔**看不见但会被真的改掉**。
   * ⇒ 存 `{ sig, ids }`，`sig` 不等于当前签名时**视为空**（派生式重置，不靠 `useEffect`）。
   */
  const [selection, setSelection] = useState<{ sig: string; ids: ReadonlySet<string> }>({
    sig: '',
    ids: EMPTY_SELECTION,
  });
  /** 批量动作的结果 / 错误（`AC-C1.21`：被拒必须逐条说原因） */
  const [batchMsg, setBatchMsg] = useState<string | null>(null);
  const [batchError, setBatchError] = useState<string | null>(null);
  /** 当前打开的是哪个批量动作面板（`null` = 没开） */
  const [batchPanel, setBatchPanel] = useState<BatchPanel>(null);
  const [bulkCategory, setBulkCategory] = useState<string | null>(null);
  const [bulkAccount, setBulkAccount] = useState<string>('');
  const [bulkTags, setBulkTags] = useState<string[]>([]);
  /** 示例数据的忙碌 / 失败态（`AC-B4.2`：默认不注入，用户点了才写） */
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoError, setDemoError] = useState<string | null>(null);
  const qc = useQueryClient();

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  // ⚠️ key 必须是 `['ledgerMembers']`（与 `SettingsPage` 同源）——
  //    写成别的会**再发一次同样的请求**，且两处缓存永不同步（成员变化后只有一处会刷新）
  const members = useQuery({ queryKey: ['ledgerMembers'], queryFn: listLedgerMembers });
  const categories = useQuery({ queryKey: ['categories'], queryFn: listCategories });
  /**
   * 🆕 批 `112-B`：**含已移除**的分类（只给筛选栏用，`AC-B3.13` 筛选侧）。
   *
   * 🔴 单独一个 key（`['categories','withRemoved']`）而不是复用上面那条：
   * 若把全局 `['categories']` 改成就含已移除，**批量改分类面板**（同页）与其它页的
   * 所有选择器都会跟着能看到已移除分类 ⇒ 直接破 `AC-B3.14`。
   * 失效链是天然的：`invalidateQueries(['categories'])` 是**前缀匹配**，两条一起刷。
   */
  const categoriesAll = useQuery({
    queryKey: ['categories', 'withRemoved'],
    queryFn: listCategoriesWithRemoved,
  });
  const tags = useQuery({ queryKey: ['tags'], queryFn: listTags });
  // 🆕 分期溯源（`AC-B2.22`）：与分期页**共用 `['installments']`** —— 建分期后回到流水页立即生效
  const installmentPlans = useQuery({ queryKey: ['installments'], queryFn: listInstallments });
  /** 交易 → 分期标签（`已分 n 期`）—— 服务端下发 sources，端侧只 join，零判断 */
  const installmentBadge = (txnId: string): string | null => {
    for (const p of installmentPlans.data?.items ?? []) {
      if (p.sources.some((s) => s.transactionId === txnId)) {
        return `已分 ${String(p.periods)} 期`;
      }
    }
    return null;
  };
  const installmentBadgePlanId = (txnId: string): string | null => {
    for (const p of installmentPlans.data?.items ?? []) {
      if (p.sources.some((s) => s.transactionId === txnId)) return p.id;
    }
    return null;
  };

  /**
   * 🆕 用户偏好（`['preferences']`）—— 本页只消费 `txnWindow`（流水默认时间窗 · `AC-N2.18`）。
   * 🔴 与 `Root` 的 `useThemeSync` / 设置页两张面板**共用同一个 queryKey** ⇒ 一处写、多处读，
   *    设置页改完窗口后回到流水页立刻生效（`invalidateQueries(['preferences'])` 已在那两处做）。
   */
  const prefs = useQuery({ queryKey: ['preferences'], queryFn: getPreferences });
  /** 基线窗口 = 偏好派生的那一档（无偏好数据 ⇒ `TxnWindowPolicy.Default` = 近 3 月） */
  const baseWindow = baseWindowOf(prefs.data?.txnWindow);
  const draft: JournalFilterDraft =
    draftOverride ??
    // 🔴 入口参数（`?statuses=unpaid`）：预算卡「含待缴」下钻过来时要**落地就筛好**（`AC-G1.14`）
    initialJournalFilter(
      (search.get('statuses') ?? '').split(',').filter((s) => s !== ''),
      baseWindow,
    );

  /**
   * 🔴 `AC-C1.49`：**当前生效的筛选条件**（面板收起时回显在工具条下方；每枚可单独移除）。
   *
   * 为什么必须有：筛选搬进下拉面板后，**收起面板 = 看不见筛了什么** ⇒ 用户会以为"结果为什么变少了"。
   * ⚠️ 只列**偏离基线**的维度（窗口等于基线档时不显示 —— 否则新用户一进页面就看到"已选 1 项"）。
   */
  const applied: readonly { key: string; label: string; clear: () => void }[] = (() => {
    const out: { key: string; label: string; clear: () => void }[] = [];
    const set = (patch: Partial<JournalFilterDraft>): void => {
      setDraft({ ...draft, ...patch });
    };
    if (draft.window !== baseWindow) {
      const label = windowOptionsFor(baseWindow).find((o) => o.id === draft.window)?.label ?? '';
      out.push({
        key: 'window',
        label: `时间 · ${label}`,
        clear: () => {
          set({ window: baseWindow });
        },
      });
    }
    if (draft.accountIds.length > 0) {
      out.push({
        key: 'accounts',
        label: `账户 · ${String(draft.accountIds.length)}`,
        clear: () => {
          set({ accountIds: [] });
        },
      });
    }
    if (draft.memberIds.length > 0) {
      out.push({
        key: 'members',
        label: `成员 · ${String(draft.memberIds.length)}`,
        clear: () => {
          set({ memberIds: [] });
        },
      });
    }
    if (draft.categoryIds.length > 0) {
      out.push({
        key: 'categories',
        label: `分类 · ${String(draft.categoryIds.length)}`,
        clear: () => {
          set({ categoryIds: [] });
        },
      });
    }
    if (draft.tagIds.length > 0) {
      out.push({
        key: 'tags',
        label: `标签 · ${String(draft.tagIds.length)}`,
        clear: () => {
          set({ tagIds: [] });
        },
      });
    }
    if (draft.amountMinText !== '' || draft.amountMaxText !== '') {
      out.push({
        key: 'amount',
        label: `金额 · ${draft.amountMinText === '' ? '不限' : draft.amountMinText}–${
          draft.amountMaxText === '' ? '不限' : draft.amountMaxText
        }`,
        clear: () => {
          set({ amountMinText: '', amountMaxText: '' });
        },
      });
    }
    if (draft.direction !== '') {
      const label = DIRECTION_OPTIONS.find((o) => o.id === draft.direction)?.label ?? '';
      out.push({
        key: 'direction',
        label: `方向 · ${label}`,
        clear: () => {
          set({ direction: '' });
        },
      });
    }
    if (draft.statuses.length > 0) {
      out.push({
        key: 'statuses',
        label: `状态 · ${String(draft.statuses.length)}`,
        clear: () => {
          set({ statuses: [] });
        },
      });
    }
    if (draft.sources.length > 0) {
      out.push({
        key: 'sources',
        label: `来源 · ${String(draft.sources.length)}`,
        clear: () => {
          set({ sources: [] });
        },
      });
    }
    if (draft.keyword !== '') {
      out.push({
        key: 'keyword',
        label: `关键词 · ${draft.keyword}`,
        clear: () => {
          set({ keyword: '' });
        },
      });
    }
    return out;
  })();

  /** 🔴 布局令牌：吸顶 `top` 必须来自**生成产物**（`var(--…)` 在客户端**静默失效** —— 铁律 36） */
  const layout = useTheme().jiaying.layout;

  /**
   * 🔴 账本时区下的「今天」—— 窗口边界全靠它。**`''` 只是给 hook 一个确定类型**：
   * `enabled` 保证账本未知时查询不会发出（不要在这里兜底成浏览器本地日）。
   */
  const today = ledger === null ? '' : localDayOf(Date.now(), ledger.timezone);
  const filterQuery = buildJournalQuery(draft, { today });

  const journal = useInfiniteQuery({
    // 🔴 queryKey 只含**筛选签名**（不含 page）—— 换筛选即换缓存，翻页在同一缓存内追加
    queryKey: ['journal', filterQuery],
    enabled: ledger !== null,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => listTransactions(buildJournalQuery(draft, { today, page: pageParam })),
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
  });

  // ─────────────── 🆕 批 107（K3）：批量选择与动作 ───────────────

  /** 🔴 筛选签名 —— 选中集与它绑定（换了筛选，旧选中集一律作废，见 `selection` 的说明） */
  const filterSig = JSON.stringify(filterQuery);
  const selected: ReadonlySet<string> = selection.sig === filterSig ? selection.ids : EMPTY_SELECTION;
  const setSelected = (ids: ReadonlySet<string>): void => {
    setSelection({ sig: filterSig, ids });
  };

  /** 已加载的全部行（合计条与全选都只看**已加载**部分 —— 没加载的没勾过） */
  const loadedRows: TransactionRowView[] = journal.data?.pages.flatMap((p) => p.items) ?? [];
  /** 🔴 合计条：唯一实现在共享层 `nucleus/txn-summary.ts`（`AC-C1.24`），本页只做形状适配 */
  const summary = summarizeSelected(loadedRows, selected);

  /** 批量写成功后的统一收口：清选中、清面板、刷列表与所有派生读模型 */
  const afterBulkWrite = (): void => {
    setSelected(EMPTY_SELECTION);
    setBatchPanel(null);
    void qc.invalidateQueries({ queryKey: ['journal'] });
    invalidateAfterTxnWrite(qc);
  };

  const batchPatch = useMutation({
    mutationFn: bulkPatchTransactions,
    onSuccess: (r) => {
      // 🔴 被拒明细**逐字来自服务端**（`AC-C1.21`），端侧只去重计数
      setBatchMsg(describeBulkResult(r));
      setBatchError(null);
      afterBulkWrite();
    },
    onError: (e: unknown) => {
      setBatchMsg(null);
      setBatchError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const batchRemove = useMutation({
    mutationFn: bulkDeleteTransactions,
    onSuccess: (r) => {
      setBatchMsg(describeBulkResult(r));
      setBatchError(null);
      afterBulkWrite();
    },
    onError: (e: unknown) => {
      setBatchMsg(null);
      setBatchError(e instanceof ApiError ? e.message : String(e));
    },
  });

  // ─────────────── 🆕 批 107（K11）：示例数据（默认不注入 · `AC-B4.2`） ───────────────

  /**
   * 示例数据的标记标签（按名字找 —— `tags.id` 是每账本随机 id，见 `contract/tag.ts`）。
   * `null` = 本账本还没有这个标签 ⇒ 也**必然**没有示例数据（不需要再探测一次）。
   */
  const demoTag = (tags.data?.items ?? []).find((t) => t.name === DEMO_TAG) ?? null;
  const demoProbe = useQuery({
    queryKey: ['journal', 'demo', demoTag?.id ?? 'none'],
    enabled: demoTag !== null,
    queryFn: () => listTransactions(demoProbeQuery(demoTag?.id ?? '')),
  });
  /** 账里还有示例数据 ⇒ 常驻横幅（判据 = 标签，不看备注文案） */
  const demoActive = (demoProbe.data?.total ?? 0) > 0;

  // 账本未知 ⇒ **不渲染任何日期/金额相关的东西**（拿默认值兜底会把"不知道"伪装成"就是它"）
  if (ledger === null) return <Loading py={4} />;

  /**
   * 🆕 导出**当前筛选**（`AC-N4.4`）—— 🔴 **入口在本页、实现归 C 域**（N 域 PRD §6.4 铁律 3）。
   *
   * 路径由 `exportCsvPath(draft, today)` 拼 —— 它与列表**共用同一个 `buildJournalQuery()`**
   * ⇒ "屏幕上看到的"与"导出的"是**同一批行**（V1-018 那个缺陷的根治形态）。
   */
  const doExport = async (): Promise<void> => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadAuthorized(exportCsvPath(draft, today), exportFileName(ExportFormat.Csv, today, ledger.id));
    } catch (e: unknown) {
      // 🔴 原样展示服务端 `message`（端侧不编错误文案）
      setExportError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  };

  const pages = journal.data?.pages.map((p) => p.items) ?? [];
  const sections = sectionizePages(pages, today);
  const total = journal.data?.pages[0]?.total ?? 0;
  const loaded = pages.reduce((n, items) => n + items.length, 0);
  /** 🔴 `AC-L3.4`：记录人徽标**只在多人账本**出现（单人账本里恒等于自己，显示它只是噪音） */
  const showRecorder = (members.data?.length ?? 0) > 1;

  // ─────────────── 🆕 批 107（K11）：示例数据的注入与清除 ───────────────

  /**
   * 「看示例数据」—— 注入 7 笔**真实记账**（`AC-B4.2`：用户点了才写）。
   *
   * 🔴 三步都走**已有接口**：`createTag`（同名复用）→ `createTransaction`（唯一写入入口）
   * → 标记靠标签关联。**不新增任何服务端路由**，也不 `insert` 旁路 ——
   * 示例数据与用户手记的数据在结构上完全一致（否则示例本身可能是一份非法数据）。
   */
  const seedDemo = async (): Promise<void> => {
    setDemoBusy(true);
    setDemoError(null);
    try {
      const tag = demoTag ?? (await createTag(DEMO_TAG));
      const plan = buildDemoTransactions({
        accounts: accounts.data?.items ?? [],
        categories: categories.data?.items ?? [],
        today,
      });
      if (plan.length === 0) {
        setDemoError('这个账本还没有可用的账户 —— 先在「账户」页建一个储蓄卡，再回来看示例。');
        return;
      }
      for (const body of plan) {
        await createTransaction({ ...body, tagIds: [tag.id] });
      }
      await qc.invalidateQueries({ queryKey: ['tags'] });
      await qc.invalidateQueries({ queryKey: ['journal'] });
      invalidateAfterTxnWrite(qc);
    } catch (e: unknown) {
      setDemoError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setDemoBusy(false);
    }
  };

  /**
   * 「清除示例数据」—— 走 `bulk-delete`（**唯一删除接口**）。
   *
   * 🔴 只删带标记的那批（不是"删掉所有流水"）：用户可能在看过示例后自己记了几笔真账，
   * 那些**必须留下**。删除是软删（30 天可恢复），余额随之回到注入前。
   */
  const clearDemo = async (): Promise<void> => {
    if (demoTag === null) return;
    setDemoBusy(true);
    setDemoError(null);
    try {
      const page = await listTransactions(demoProbeQuery(demoTag.id, 200));
      const ids = page.items.map((r) => r.id);
      if (ids.length > 0) await bulkDeleteTransactions(ids);
      await qc.invalidateQueries({ queryKey: ['journal'] });
      invalidateAfterTxnWrite(qc);
    } catch (e: unknown) {
      setDemoError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setDemoBusy(false);
    }
  };

  // ─────────────── 批量动作的四个触发口（权限一律由服务端判） ───────────────

  const runPatch = (body: Parameters<typeof bulkPatchTransactions>[0]): void => {
    batchPatch.mutate(body);
  };

  return (
    <Box ref={contentRef}>
      <AnchorBar contentRef={contentRef} />

      {/*
        🆕 批 107（K11）：示例数据**常驻**提示（`AC-B4.2`）。
        🔴 为什么必须常驻：示例数据是**真实记账**（会进报表 / 预算 / 净资产），
        用户看完忘了清，"家里这个月花了 300 多"就是假的。横幅给的是**一键清除**，
        而不是"请自己一条条找出来删"。
      */}
      {demoActive && (
        <Alert
          severity="info"
          sx={{ mb: 1.5 }}
          data-testid="journal-demo-banner"
          action={
            <Button
              size="small"
              color="inherit"
              disabled={demoBusy}
              data-testid="journal-demo-clear"
              onClick={() => {
                void clearDemo();
              }}
            >
              清除示例数据
            </Button>
          }
        >
          这些是<b>示例数据</b>（会参与报表与净资产统计）—— 看完可以一键清除，只删带「
          {DEMO_TAG}
          」标签的那几笔，你自己记的会保留。
        </Alert>
      )}

      {demoError !== null && (
        <Alert severity="error" sx={{ mb: 1.5 }} data-testid="journal-demo-error">
          {demoError}
        </Alert>
      )}
      {/* 🔴 2026-10-14（`DR-042` · 负责人要求「筛选不占左侧栏 · 吸顶 · 下拉筛选」）：
          原 `Grid`（240–280px 筛选轨 + 列表）**已删除** ⇒ **列表全宽**；筛选收进「吸附工具条 + 下拉面板」，
          该面板即**全页唯一筛选入口**（覆盖 10 维 · `AC-C1.47`）。

          🔴 **2026-10-14 二次修正（`DR-042` · 负责人报「第一条流水被吸顶的筛选覆盖」）**：
          **吸顶必须由 `Card` 之外的这一层承担。** 原因（CDP 无头实测，见 changelog 2026-10-14-b72）：
          `Card` = `MuiPaper` ⇒ `overflow: hidden`，它会被浏览器当成**滚动容器**（`scrollTop` 恒 0）
          ⇒ 写在工具条自身的 `position: sticky; top: 58px` 不再是"吸附阈值"，而变成**恒定的 58px 下推**：
          工具条被从 90 推到 **148**，而列表首行从 **142** 开始 ⇒ **静止状态下就重叠 6px、首行被吃掉 31px**。
          ⇒ 把 sticky 提到**没有裁剪祖先**的 wrapper 上；`Card` 退回纯外观容器（圆角 / 纸面）。
          （同族判据见铁律 43：几何结论只认无头取证 —— 单测怎么写都绿。） */}
      <Box
        data-testid="journal-sticky-bar"
        sx={{
          // 🔴 面板展开时**不吸顶**：面板本身很高，若跟着吸顶会把列表整段遮住
          //    （收起态才吸附 · `AC-C1.48`）
          position: panelOpen ? 'static' : 'sticky',
          // 🔴 取**布局令牌**而非 `var(--topbar-h)`：CSS 变量在本客户端下**静默失效**（铁律 36）
          top: layout.topbarH,
          zIndex: 3,
        }}
      >
        <Card>
        {/* ── 吸附工具条（`AC-C1.48`）—— 自身**不 sticky**，吸附由外层 wrapper 承担 ── */}
        <Box
          data-testid="journal-toolbar"
          sx={{
            px: 2,
            pt: 1.5,
            pb: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {/*
              🔴 筛选入口（下拉面板开关）。计数只报**已生效的维度数**（`activeDimensionCount`）——
              默认档「近 3 月」不算进去（它本来就是开着的，算了会让新用户看到"已筛选 1 项"）。

              🆕 批 `109`（负责人 2026-10-26：「筛选可以选择账户，日期，金额，然后再加最后的搜索框」）：
              工具条顺序改为 **筛选 → 统计 / 导出 → 搜索框**，且面板内维度顺序固定为
              **账户 → 时间 → 金额区间** 打头（见 `ui/JournalFilterRail.tsx` 的 `facet-grid`）。
              ⇒ "先按维度筛，最后才用关键词收口"这个心智与屏幕从左到右一致。
            */}
            <Button
              size="small"
              variant={panelOpen ? 'contained' : 'outlined'}
              data-testid="journal-filter-toggle"
              onClick={() => {
                setPanelOpen((v) => !v);
              }}
            >
              {activeDimensionCount(draft, baseWindow) === 0
                ? '筛选'
                : `筛选 · ${String(activeDimensionCount(draft, baseWindow))}`}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary" data-testid="journal-summary">
              共 {total} 笔 ·{' '}
              {windowOptionsFor(baseWindow).find((o) => o.id === draft.window)?.label ?? ''} · 每批 50
            </Typography>
            {/* 🆕 导出**当前筛选**（`AC-N4.4`）—— 入口归 N 域、实现归 C 域（铁律 3） */}
            <Button
              size="small"
              variant="outlined"
              disabled={exporting}
              data-testid="journal-export"
              onClick={() => {
                void doExport();
              }}
            >
              {exporting ? EXPORT_BUSY_LABEL : EXPORT_CURRENT_ACTION}
            </Button>
            {/* 🔴 搜索框**放最后**（批 109）—— 它是"筛完再收口"的那一步，不是起点 */}
            <TextField
              size="small"
              placeholder="搜金额、备注、商户…"
              value={draft.keyword}
              onChange={(e) => {
                setDraft({ ...draft, keyword: e.target.value });
              }}
              inputProps={{ 'data-testid': 'journal-keyword' }}
              sx={{ maxWidth: 260, minWidth: 200 }}
            />
          </Stack>

          {/*
            🆕 批 107（K3）：批量工具条（`AC-C1.19`–`C1.24`）。
            🔴 **没选中时不渲染**（`AC`: 不摆一个灰掉的条）—— 空条会让页面看起来"有个功能坏了"。
            🔴 动作按钮**不置灰**：能不能对某一笔生效由**服务端**逐条判并逐条给原因（`AC-C1.21`）。
               端侧另造一份"多腿 ⇒ 置灰"的规则，会在服务端改判据时**静默说谎**。
          */}
          {selected.size > 0 && (
            <Box data-testid="journal-batch-bar" sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                {/*
                  🔴 MUI `Checkbox` 的 `inputProps` 不接 `data-*`（铁律 100）⇒ testid 挂在包裹元素上。
                  「全选本页」= 并进选中集（不清掉之前翻页勾过的）。
                */}
                <Box component="span" data-testid="journal-batch-all">
                  <Checkbox
                    size="small"
                    checked={isAllLoadedSelected(selected, loadedRows.map((r) => r.id))}
                    onChange={(e) => {
                      const ids = loadedRows.map((r) => r.id);
                      setSelected(e.target.checked ? selectAllLoaded(selected, ids) : EMPTY_SELECTION);
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }} data-testid="journal-batch-count">
                  已选 {selected.size} 笔
                </Typography>
                <Button size="small" variant="outlined" data-testid="journal-batch-category" onClick={() => { setBatchPanel('category'); }}>
                  改分类
                </Button>
                <Button size="small" variant="outlined" data-testid="journal-batch-account" onClick={() => { setBatchPanel('account'); }}>
                  改账户
                </Button>
                <Button size="small" variant="outlined" data-testid="journal-batch-tags" onClick={() => { setBatchPanel('tags'); }}>
                  加标签
                </Button>
                <Button size="small" variant="outlined" data-testid="journal-batch-untags" onClick={() => { setBatchPanel('untags'); }}>
                  去标签
                </Button>
                <Button size="small" variant="outlined" color="error" data-testid="journal-batch-delete" onClick={() => { setBatchPanel('delete'); }}>
                  删除
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                  size="small"
                  variant="text"
                  data-testid="journal-batch-cancel"
                  onClick={() => {
                    setSelected(EMPTY_SELECTION);
                    setBatchPanel(null);
                  }}
                >
                  取消选择
                </Button>
              </Stack>

              {/*
                🆕 合计条（`AC-C1.24` / C 域 PRD §5.1.9）。
                🔴 数字全部来自 `nucleus/txn-summary.ts` 的 `summarizeSelection()`：
                按**方向分列**（不做净额）、按**币种分组**（不折算）、不计收支**单列**。
                本页一行加法都不写。
              */}
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="baseline"
                sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}
                data-testid="journal-batch-summary"
              >
                {summary.byCurrency.map((c) => (
                  <Box key={c.currency} component="span" data-testid={`journal-batch-sum-${c.currency}`}>
                    <Typography variant="caption" color="text.secondary" component="span">
                      {c.currency} 流入{' '}
                    </Typography>
                    <Amount component="span" variant="caption" cents={c.inflow} currency={c.currency} colored={false} />
                    <Typography variant="caption" color="text.secondary" component="span">
                      {' '}
                      · 流出{' '}
                    </Typography>
                    <Amount component="span" variant="caption" cents={c.outflow} currency={c.currency} colored={false} />
                    {c.excluded > 0 && (
                      <>
                        <Typography variant="caption" color="text.secondary" component="span">
                          {' '}
                          · 另有{' '}
                        </Typography>
                        <Amount component="span" variant="caption" cents={c.excluded} currency={c.currency} colored={false} />
                        <Typography variant="caption" color="text.secondary" component="span">
                          {' '}
                          不计收支
                        </Typography>
                      </>
                    )}
                  </Box>
                ))}
              </Stack>

              {/*
                多腿提示（**说明性**，不是权限判定）：告诉用户这批里有转账 / 工资条 / 拆分，
                结构敏感的动作用服务端会逐条拒 —— 免得点完才发现一半没改成。
              */}
              {multiLegCount(loadedRows, selected) > 0 && (
                <Typography variant="caption" color="text.secondary" data-testid="journal-batch-multileg">
                  所选中含 {multiLegCount(loadedRows, selected)} 笔多腿交易（转账 / 工资条 / 拆分）——
                  改分类与改账户会被服务端逐条拒绝并说明原因，加 / 去标签与删除不受影响。
                </Typography>
              )}
            </Box>
          )}

          {/* 批量结果（🔴 被拒明细逐字来自服务端 · `AC-C1.21`） */}
          {batchMsg !== null && (
            <Alert severity="success" sx={{ mt: 1 }} data-testid="journal-batch-result">
              {batchMsg}
            </Alert>
          )}
          {batchError !== null && (
            <Alert severity="error" sx={{ mt: 1 }} data-testid="journal-batch-error">
              {batchError}
            </Alert>
          )}

          {/* 批量动作面板（🔴 内联面板，不用 `Dialog` —— Portal 让静态渲染拿不到内容，铁律 121） */}
          {batchPanel !== null && selected.size > 0 && (
            <Box sx={{ mt: 1, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }} data-testid="journal-batch-panel">
              {batchPanel === 'category' && (
                <CategoryPicker
                  kind="expense"
                  items={categories.data?.items ?? []}
                  value={bulkCategory}
                  onChange={setBulkCategory}
                  label="把所选的分类改成"
                />
              )}
              {batchPanel === 'account' && (
                <AccountPicker
                  accounts={accounts.data?.items ?? []}
                  value={bulkAccount}
                  onChange={setBulkAccount}
                  label="把所选移到账户"
                />
              )}
              {(batchPanel === 'tags' || batchPanel === 'untags') && (
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }} data-testid="journal-batch-taglist">
                  {(tags.data?.items ?? []).map((t) => {
                    const on = bulkTags.includes(t.id);
                    return (
                      <Chip
                        key={t.id}
                        size="small"
                        variant={on ? 'filled' : 'outlined'}
                        label={t.name}
                        data-testid={`journal-batch-tag-${t.id}`}
                        onClick={() => {
                          setBulkTags(on ? bulkTags.filter((x) => x !== t.id) : [...bulkTags, t.id]);
                        }}
                      />
                    );
                  })}
                </Stack>
              )}
              {batchPanel === 'delete' && (
                <Typography variant="body2" data-testid="journal-batch-delete-hint">
                  将删除所选的 {selected.size} 笔流水。<b>30 天内可在「状态 → 已删除」里恢复</b>，
                  账户余额会同步回退。
                </Typography>
              )}

              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                {batchPanel === 'delete' ? (
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    disabled={batchRemove.isPending}
                    data-testid="journal-batch-delete-confirm"
                    onClick={() => {
                      batchRemove.mutate([...selected]);
                    }}
                  >
                    确认删除
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={batchPatch.isPending}
                    data-testid="journal-batch-apply"
                    onClick={() => {
                      const ids = [...selected];
                      if (batchPanel === 'category') {
                        runPatch({ ids, action: 'setCategory', categoryId: bulkCategory });
                      } else if (batchPanel === 'account') {
                        runPatch({ ids, action: 'setAccount', accountId: bulkAccount });
                      } else if (batchPanel === 'tags') {
                        runPatch({ ids, action: 'addTags', tagIds: bulkTags });
                      } else {
                        runPatch({ ids, action: 'removeTags', tagIds: bulkTags });
                      }
                    }}
                  >
                    应用
                  </Button>
                )}
                <Button size="small" data-testid="journal-batch-panel-cancel" onClick={() => { setBatchPanel(null); }}>
                  收起
                </Button>
              </Stack>
            </Box>
          )}

          {/*
            🔴 `AC-C1.49` **已选条件回显** —— 筛选搬进下拉面板后，收起面板就看不见筛了什么
            ⇒ 用户会以为"结果为什么变少了"。每枚 chip 可**单独移除**，右侧另有**一键清空**。
          */}
          {applied.length > 0 ? (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}
              data-testid="journal-applied"
            >
              <Typography variant="caption" color="text.secondary">
                已选
              </Typography>
              {applied.map((f) => (
                <Chip
                  key={f.key}
                  size="small"
                  label={f.label}
                  onDelete={f.clear}
                  data-testid={`applied-${f.key}`}
                />
              ))}
              <Button
                size="small"
                variant="text"
                data-testid="journal-applied-clear"
                onClick={() => {
                  // 🔴 清空 = 回到**用户的基线**（含他设的默认时间窗），不是硬编码的「近 3 月」
                  setDraft(initialJournalFilter([], baseWindow));
                }}
              >
                清空
              </Button>
            </Stack>
          ) : null}
        </Box>

        {/* ── 筛选下拉面板（🔴 **唯一筛选入口** · 覆盖 10 维 · `AC-C1.47`）── */}
        {/*
          ⚠️ **刻意不用 `unmountOnExit`**：收起时内容仍留在 DOM（仅高度 0）——
          `renderToStaticMarkup` 基座**拿不到"展开后"的产物**（静态渲染不能点击），
          用 `unmountOnExit` 会让"面板含 10 维"这类断言**永久无法断言**。
        */}
        <Collapse in={panelOpen} timeout="auto">
          <Box
            sx={{
              px: 2,
              pt: 1,
              pb: 1.5,
              bgcolor: 'action.hover',
              // 🔴 2026-10-14：防「占满整屏」（负责人反馈）—— 面板自身滚动。
              //    两列网格 + 分类横排后实测高度已大幅下降，此上限是小屏 / 多开 L2 时的兜底。
              maxHeight: 'min(62vh, 560px)',
              overflowY: 'auto',
            }}
            data-testid="journal-filter-panel"
          >
            <JournalFilterRail
              draft={draft}
              onChange={setDraft}
              accounts={accounts.data?.items ?? []}
              members={members.data ?? []}
              // 🔴 只**这一处**传含已移除的列表（筛选要能按历史分类筛）；
              // 批量改分类面板、Demo 造数据仍用上面的 `categories`（可见集）——
              // 传错一处就等于让「已移除」重新出现在选择器里（`AC-B3.14`）。
              // 回退到 `categories` 是为了「含已移除」这条请求失败时面板仍有可用分类。
              categories={categoriesAll.data?.items ?? categories.data?.items ?? []}
              tags={tags.data?.items ?? []}
              amountError={amountInputError(draft)}
              // 🆕 「默认」标注跟着**这个用户的**基线窗口走（`AC-N2.18`）
              baseWindow={baseWindow}
            />
          </Box>
        </Collapse>

        {exportError !== null ? (
          <Alert severity="error" sx={{ mx: 2, my: 1 }} data-testid="journal-export-error">
            {EXPORT_FAILED_PREFIX}
            {exportError}
          </Alert>
        ) : null}
        </Card>
      </Box>

      {/* ── 流水列表（独立 Card · 与外层吸附条**分开**才不会互相盖住）── */}
      <Card data-anchor="流水列表" sx={{ mt: 2 }}>

          {journal.isPending && <Loading py={4} />}
          {journal.isError && (
            <Typography variant="body2" color="error" sx={{ p: 2 }} data-testid="journal-error">
              流水加载失败，请重试
            </Typography>
          )}

          {journal.isSuccess && sections.length === 0 && (
            <Box sx={{ p: 3 }} data-testid="journal-empty">
              {/*
                🆕 批 107（K11 · `AC-B4.7`）：空态**必须给下一步动作**。
                🔴 两种空态必须分开，因为用户的处境不同：
                · `total === 0` ⇒ **真空账本**（新人），要的是"从哪开始"；
                · `total > 0` ⇒ 只是**筛得太窄**，要的是"放宽条件"。
                给错动作等于把人往错的方向推（新人看到"清空筛选"会以为自己筛错了）。
              */}
              {total === 0 ? (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    还一笔流水都没有
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    可以先看一组示例（7 笔，含收入 / 支出 / 转账 / 信用卡），也可以直接记第一笔。
                    示例随时能一键清除。
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={demoBusy}
                      data-testid="journal-empty-demo"
                      onClick={() => {
                        void seedDemo();
                      }}
                    >
                      看示例数据
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      data-testid="journal-empty-first"
                      onClick={() => {
                        navigate('/new');
                      }}
                    >
                      记第一笔
                    </Button>
                  </Stack>
                </>
              ) : (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    这个筛选下还没有流水
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    可能条件太窄 —— 试试放宽日期范围，或清空筛选。
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      data-testid="journal-empty-clear"
                      onClick={() => {
                        // 🔴 清空 = 回到**用户的基线**（含他设的默认时间窗），不是硬编码的「近 3 月」
                        setDraft(initialJournalFilter([], baseWindow));
                      }}
                    >
                      清空筛选
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}

          {journal.isSuccess &&
            sections.map((section, idx) => (
              // 🔴 `key` 含下标：同一个日期**允许出现两次**（跨批次重复分组头，`AC-C1.5`）
              //    ⇒ 只用 `date` 做 key 会撞键（React 会静默复用节点，表现为"少了一行"）
              <Box key={`${section.date}-${String(idx)}`} data-testid={`section-${section.date}`}>
                {/*
                  🆕 批次 `94`：日期头从「一行 11px 灰字」升级为**分隔条**。

                  原因（负责人 2026-10-21）：日期头与第一行流水贴在一起，一天的边界看不出来。

                  🔴 `background.default`（= 页面底色 `c-bg`）与列表所在的 `Card`（`c-surface`）
                  是两个不同令牌 ⇒ 明暗两套下都天然有对比，**不写任何色值**。
                  🔴 第一段不加顶边线/顶距 —— 否则列表顶上会多出一条悬空的线。
                  ⚠️ 不用 `sticky`：外层吸附条（`AnchorBar`）已是 sticky，祖先裁剪会让这里退化（铁律 81）。
                */}
                <Box
                  sx={{
                    px: 2,
                    py: 0.75,
                    bgcolor: 'background.default',
                    borderTop: idx === 0 ? 'none' : '1px solid',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {section.label}
                  </Typography>
                </Box>
                <List dense disablePadding>
                  {section.items.map((row) => (
                    <Fragment key={row.id}>
                    <ListItem
                      divider
                      data-testid={`row-${row.id}`}
                      // 点行开抽屉（`AC-C1.33`）—— 不是新页面
                      onClick={() => {
                        setSelectedId(row.id);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      {/*
                        🆕 批 107（K3）：行首勾选框（`AC-C1.19`）。
                        🔴 选中态以 **`transactionId`** 为准（不是行下标 —— 翻页 / 刷新后下标会整体位移）。
                        🔴 包裹层 `stopPropagation`：点勾选框**不能**顺带打开详情抽屉。
                      */}
                      <Box
                        component="span"
                        data-testid={`journal-pick-${row.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={selected.has(row.id)}
                          onChange={() => {
                            setSelected(toggleSelection(selected, row.id));
                          }}
                        />
                      </Box>
                      {/*
                        🆕 批 `115`（T7 · `AC-B5.6`）：**多笔构成的行内折叠**。
                        🔴 只对 `legs.length > 1` 的行渲染 —— 单腿行给一个展开按钮是**无意义的点击**
                        （点开只会看到和行上一模一样的一条）。
                        🔴 位置在勾选框右侧（负责人要的是"行上就能看构成"，不是塞进 Amount 那一列）；
                        `stopPropagation` 必须有，否则点它顺带打开详情抽屉。
                        🔴 展开态**不持久化**、按 `transactionId` 记（翻页/刷新后下标会位移）。
                      */}
                      {row.legs.length > 1 && (
                        <IconButton
                          size="small"
                          aria-expanded={expandedRows.has(row.id)}
                          aria-label={expandedRows.has(row.id) ? '收起这笔的构成' : '展开这笔的构成'}
                          data-testid={`row-expand-${row.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedRows((prev) => {
                              const next = new Set(prev);
                              if (next.has(row.id)) next.delete(row.id);
                              else next.add(row.id);
                              return next;
                            });
                          }}
                          sx={{ p: 0.25, mr: 0.5 }}
                        >
                          <Box component="span" sx={{ fontSize: 14, lineHeight: 1 }}>
                            {expandedRows.has(row.id) ? '▾' : '▸'}
                          </Box>
                        </IconButton>
                      )}
                      <ListItemText
                        primary={rowTitle(row)}
                        secondary={
                          <Stack direction="row" spacing={0.75} alignItems="center" component="span">
                            <span>{rowSubtitle(row)}</span>
                            {excludedBadge(row) !== null && (
                              // 🔴 `AC-C1.7`：转账与其它不参与收支的笔**必须标注**
                              <Chip
                                size="small"
                                variant="outlined"
                                label={excludedBadge(row) ?? ''}
                                data-testid={`excluded-${row.id}`}
                              />
                            )}
                            {installmentBadge(row.id) !== null && (
                              // 🆕 `AC-B2.22`（`DR-060` D5）：被分期覆盖的消费**必须**显示「已分 n 期」
                              //    且可跳到计划（溯源双向可查的流水侧）。数据 = `listInstallments`
                              //    的 sources（服务端下发），端侧只做 join，零判断
                              <Chip
                                size="small"
                                variant="outlined"
                                color="primary"
                                label={installmentBadge(row.id) ?? ''}
                                data-testid={`installment-tag-${row.id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const planId = installmentBadgePlanId(row.id);
                                  if (planId !== null) navigate(`/installments/${planId}`);
                                }}
                              />
                            )}
                            {showRecorder && (
                              // 🔴 `AC-L3.4`：记录人徽标（≠ 归属人 —— 归属人在抽屉里，见 `AC-L3.3`）
                              <Chip
                                size="small"
                                variant="outlined"
                                label={row.userName}
                                data-testid={`recorder-${row.id}`}
                              />
                            )}
                            {/*
                              🆕 批 110：**填过优惠就必须看得见**。
                              🔴 修前 `discount` 一路通到端侧却**没有任何地方渲染** ——
                              用户填完回列表看不到，会认为"功能没了"（负责人 2026-10-26 反馈）。
                              🔴 放在 secondary 这排（不进金额那一列）⇒ **不改变行高**，
                              不会破坏"每行等高"；金额仍是实付净额（`DR-040`：amount 恒为实付）。
                            */}
                            {rowDiscount(row) > 0 && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                data-testid={`row-discount-${row.id}`}
                              >
                                优惠 {formatMoneyText(rowDiscount(row), row.parent.currency)}
                              </Typography>
                            )}
                            {/*
                              🆕 批 `120`（`DR-069` D3）：**被退过的消费必须看得出真实支出**。
                              修前只显示全额，用户以为这笔花了 175.50 —— 而报表里早就是 125.50。
                              🔴 金额列同时改成净额（`rowAmount`），这里补「已退多少 · 原额多少」。
                            */}
                            {refundHint(row) !== null && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                data-testid={`row-refund-hint-${row.id}`}
                              >
                                {refundHint(row)}
                              </Typography>
                            )}
                            {/*
                              🆕 批 `120`（`DR-069` D5）：退款行必须说清**冲的是哪一笔** ——
                              修前它与普通收入行完全同形，只有点开抽屉才能看到「冲减自…」。
                            */}
                            {chargeBackHint(row) !== null && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                data-testid={`row-chargeback-${row.id}`}
                              >
                                {chargeBackHint(row)}
                              </Typography>
                            )}
                          </Stack>
                        }
                      />
                      {/*
                        🔴 金额只取服务端派生的 `parent`（转账取 `fromAmount` 且不着色、其余取 `net`）——
                        规则**抽在共享层** `rowAmount()`，与报表下钻抽屉同一实现（`AC-K2.9` 防两套口径）
                      */}
                      {/*
                        🔴 批 `120`（`DR-069` D4）：**全额退完显示「已全额退款」**而不是 `¥0.00` ——
                        `¥0.00` 会被读成"没花钱"或"数据坏了"，而全额退完其实是一笔已结清的消费。
                      */}
                      {isFullyRefunded(row) ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                          data-testid={`row-fully-refunded-${row.id}`}
                        >
                          已全额退款
                        </Typography>
                      ) : (
                        <Amount
                          cents={rowAmount(row).cents}
                          currency={row.parent.currency}
                          colored={rowAmount(row).colored}
                          signed={rowAmount(row).signed}
                          variant="body2"
                        />
                      )}
                      {/*
                        🆕 批 `115`（T6 · `AC-B5.12`）：**流水页直接退款** —— 修前要先回首页才点得到。
                        🔴 判据 = `canRefund(row)`（与首页**同一个函数**）：支出行有；收入 / 转账 / 退款行都没有。
                        🔴 `stopPropagation` 必须有：否则点「退款」会顺带打开详情抽屉（两件事同时发生）。
                        🔴 跳转形状照抄首页的 `state.refund`（`RefundSource` 契约）——
                        这是同一个入口的第二个按钮，不是第二套退款流程。
                      */}
                      {canRefund(row) && (
                        <Button
                          size="small"
                          data-testid={`row-refund-${row.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            void navigate('/new', {
                              state: {
                                refund: {
                                  id: row.id,
                                  accountId: row.parent.mainAccountId,
                                  note: row.note,
                                  date: row.date,
                                  net: row.parent.net,
                                  // 🔴 批 `119`：退款腿要挂**原支出分类**（`isRefundTxn` 的判据），
                                  //    取值口径的唯一实现 = `refundCategoryId()`
                                  categoryId: refundCategoryId(row),
                                  // 🆕 批 `120`（`DR-069` D6）：已退多少 —— 退款页算「最多可退」用
                                  refundedAmount: row.refunds?.amount ?? 0,
                                },
                              },
                            });
                          }}
                          sx={{ ml: 1, whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                          退款
                        </Button>
                      )}
                    </ListItem>
                    {/*
                      🔴 折叠区渲染在**行下方**（`ListItem` 之外）—— 塞进 `ListItem` 内部会让
                      `divider` 错位（批 `110` 踩过同类）。
                      🔴 `maxHeight: 240 · overflowY: auto`：多笔行不设上限会把整行撑高，
                      屏上只剩一两笔可见。
                    */}
                    {row.legs.length > 1 && (
                      <Collapse in={expandedRows.has(row.id)} timeout="auto" unmountOnExit>
                        <Box
                          data-testid={`row-legs-${row.id}`}
                          sx={{
                            pl: 7,
                            pr: 2,
                            pb: 1,
                            bgcolor: 'background.default',
                            maxHeight: 240,
                            overflowY: 'auto',
                          }}
                        >
                          <TxnLegsList legs={row.legs} variant="compact" />
                        </Box>
                      </Collapse>
                    )}
                    </Fragment>
                  ))}
                </List>
              </Box>
            ))}

          {/* ── 「加载更多」（§5.1.3：非页码）── */}
          {journal.isSuccess && loaded > 0 && (
            <Box sx={{ p: 1.5, textAlign: 'center' }}>
              <Button
                size="small"
                variant="outlined"
                data-testid="journal-load-more"
                disabled={!journal.hasNextPage || journal.isFetchingNextPage}
                onClick={() => {
                  void journal.fetchNextPage();
                }}
              >
                {journal.hasNextPage ? `加载更多（已加载 ${String(loaded)} / ${String(total)}）` : '已全部加载'}
              </Button>
            </Box>
          )}
      </Card>

      {/* 详情抽屉（`AC-C1.33` / `AC-L3.5`）—— 归因三行由服务端 `attribution` 下发 */}
      <TransactionDrawer
        transactionId={selectedId}
        timezone={ledger.timezone}
        // 🆕 批 `115`（T3 第 4 条）：**显式传**回程票，不依赖默认值
        returnTo="/journal"
        onClose={() => {
          setSelectedId(null);
        }}
      />
    </Box>
  );
}
